import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTierGate } from "@/hooks/useTierGate";
import { PaywallModal } from "@/components/PaywallModal";
import PlusBadge from "@/components/PlusBadge";

const COUNTRY_CODES = [
  { value: "+852", label: "🇭🇰 +852" },
  { value: "+1", label: "🇺🇸 +1" },
  { value: "+44", label: "🇬🇧 +44" },
  { value: "+61", label: "🇦🇺 +61" },
  { value: "+65", label: "🇸🇬 +65" },
  { value: "+81", label: "🇯🇵 +81" },
  { value: "+86", label: "🇨🇳 +86" },
  { value: "+91", label: "🇮🇳 +91" },
  { value: "+49", label: "🇩🇪 +49" },
  { value: "+33", label: "🇫🇷 +33" },
  { value: "+82", label: "🇰🇷 +82" },
  { value: "+60", label: "🇲🇾 +60" },
  { value: "+63", label: "🇵🇭 +63" },
  { value: "+66", label: "🇹🇭 +66" },
  { value: "+234", label: "🇳🇬 +234" },
];

const PHONE_LENGTH_MAP: Record<string, number> = {
  '+1': 10, '+7': 10, '+20': 10, '+27': 9, '+30': 10, '+31': 9, '+32': 9,
  '+33': 9, '+34': 9, '+39': 10, '+40': 9, '+41': 9, '+44': 10, '+45': 8,
  '+46': 9, '+47': 8, '+48': 9, '+49': 10, '+52': 10, '+54': 10, '+55': 11,
  '+56': 9, '+57': 10, '+60': 9, '+61': 9, '+62': 10, '+63': 10, '+64': 9,
  '+65': 8, '+66': 9, '+81': 10, '+82': 10, '+84': 9, '+86': 11, '+90': 10,
  '+91': 10, '+92': 10, '+94': 9, '+95': 9, '+234': 10, '+254': 9, '+255': 9,
  '+256': 9, '+260': 9, '+263': 9, '+351': 9, '+352': 9, '+353': 9, '+358': 9,
  '+380': 9, '+420': 9, '+421': 9, '+852': 8, '+853': 8, '+855': 9, '+856': 9,
  '+880': 10, '+886': 9, '+960': 7, '+966': 9, '+971': 9, '+972': 9, '+973': 8,
  '+974': 8, '+975': 8, '+976': 8, '+977': 10, '+992': 9, '+993': 8, '+994': 9,
  '+995': 9, '+996': 9, '+998': 9,
};

const COUNTRY_NAME_MAP: Record<string, string> = {
  '+1': 'US/Canada', '+7': 'Russia', '+20': 'Egypt', '+27': 'South Africa',
  '+30': 'Greece', '+31': 'Netherlands', '+32': 'Belgium', '+33': 'France',
  '+34': 'Spain', '+39': 'Italy', '+40': 'Romania', '+41': 'Switzerland',
  '+44': 'UK', '+45': 'Denmark', '+46': 'Sweden', '+47': 'Norway',
  '+48': 'Poland', '+49': 'Germany', '+52': 'Mexico', '+54': 'Argentina',
  '+55': 'Brazil', '+56': 'Chile', '+57': 'Colombia', '+60': 'Malaysia',
  '+61': 'Australia', '+62': 'Indonesia', '+63': 'Philippines', '+64': 'New Zealand',
  '+65': 'Singapore', '+66': 'Thailand', '+81': 'Japan', '+82': 'South Korea',
  '+84': 'Vietnam', '+86': 'China', '+90': 'Turkey', '+91': 'India',
  '+92': 'Pakistan', '+94': 'Sri Lanka', '+95': 'Myanmar', '+234': 'Nigeria',
  '+254': 'Kenya', '+255': 'Tanzania', '+256': 'Uganda', '+260': 'Zambia',
  '+263': 'Zimbabwe', '+351': 'Portugal', '+352': 'Luxembourg', '+353': 'Ireland',
  '+358': 'Finland', '+380': 'Ukraine', '+420': 'Czech Republic', '+421': 'Slovakia',
  '+852': 'Hong Kong', '+853': 'Macau', '+855': 'Cambodia', '+856': 'Laos',
  '+880': 'Bangladesh', '+886': 'Taiwan', '+960': 'Maldives', '+966': 'Saudi Arabia',
  '+971': 'UAE', '+972': 'Israel', '+973': 'Bahrain', '+974': 'Qatar',
  '+975': 'Bhutan', '+976': 'Mongolia', '+977': 'Nepal', '+992': 'Tajikistan',
  '+993': 'Turkmenistan', '+994': 'Azerbaijan', '+995': 'Georgia', '+996': 'Kyrgyzstan',
  '+998': 'Uzbekistan',
};

function maskNumber(fullNumber: string): string {
  for (const cc of COUNTRY_CODES.map((c) => c.value).sort((a, b) => b.length - a.length)) {
    if (fullNumber.startsWith(cc)) {
      const local = fullNumber.slice(cc.length);
      if (local.length > 4) {
        const masked = "•".repeat(local.length - 4) + local.slice(-4);
        return `${cc} ${masked.replace(/(.{4})/g, "$1 ").trim()}`;
      }
      return `${cc} ${local}`;
    }
  }
  if (fullNumber.length > 4) {
    return "•".repeat(fullNumber.length - 4) + fullNumber.slice(-4);
  }
  return fullNumber;
}

type WhatsAppState = "not_opted_in" | "pending" | "confirmed";

interface WhatsAppOptInPanelProps {
  whatsappNumber: string | null;
  whatsappConfirmed: boolean;
  onStateChange: () => void;
}

const WhatsAppOptInPanel = ({
  whatsappNumber,
  whatsappConfirmed,
  onStateChange,
}: WhatsAppOptInPanelProps) => {
  const { user } = useAuth();
  const { canAccess } = useTierGate();
  const hasWhatsApp = canAccess('whatsappReminders');
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [countryCode, setCountryCode] = useState("+852");
  const [phoneInput, setPhoneInput] = useState("");
  const [sending, setSending] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [showTurnOffConfirm, setShowTurnOffConfirm] = useState(false);
  const [turningOff, setTurningOff] = useState(false);

  // Determine state from profiles fields only
  const state: WhatsAppState = whatsappConfirmed
    ? "confirmed"
    : whatsappNumber
      ? "pending"
      : "not_opted_in";

  // Poll for confirmation when pending
  useEffect(() => {
    if (state !== "pending") return;

    const interval = setInterval(async () => {
      if (!user) return;
      const { data } = await supabase
        .from("profiles")
        .select("whatsapp_confirmed")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data?.whatsapp_confirmed) {
        onStateChange();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [state, user, onStateChange]);

  const checkRateLimit = async (): Promise<boolean> => {
    if (!user) return false;

    // Admins bypass rate limit
    const { data: isAdmin } = await supabase.rpc("is_admin", { _user_id: user.id });
    if (isAdmin) return true;

    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

    const { count } = await supabase
      .from("whatsapp_number_changes")
      .select("*", { count: "exact", head: true })
      .eq("user_id", user.id)
      .gte("changed_at", sixtyDaysAgo.toISOString());

    if ((count ?? 0) >= 3) {
      setRateLimitError(
        "You've updated your WhatsApp number several times recently. Please wait a few days before trying again."
      );
      return false;
    }
    setRateLimitError(null);
    return true;
  };

  const handleToggle = (checked: boolean) => {
    if (!hasWhatsApp) {
      setPaywallOpen(true);
      return;
    }
    if (checked) {
      if (state === "not_opted_in") {
        setShowPanel(true);
      }
    } else {
      if (state === "confirmed") {
        setShowTurnOffConfirm(true);
      } else if (state === "pending") {
        // Cancel pending
        handleTurnOff();
      }
    }
  };

  const handleSendConfirmation = async () => {
    if (!user) return;
    const cleanNumber = phoneInput.replace(/\s/g, "");
    if (cleanNumber.length < 7 || !/^\d+$/.test(cleanNumber)) {
      toast.error("Please enter a valid phone number (minimum 7 digits).");
      return;
    }

    setSending(true);
    const allowed = await checkRateLimit();
    if (!allowed) {
      setSending(false);
      return;
    }

    const fullNumber = `${countryCode}${cleanNumber}`;

    try {
      const { error } = await supabase.functions.invoke("send-whatsapp-confirmation", {
        body: { phoneNumber: fullNumber },
      });

      if (error) {
        let statusCode = error.status;
        let errorMessage: string | undefined;

        try {
          const response = error.context as Response | undefined;
          if (response && typeof response.clone === "function") {
            statusCode = response.status;
            const parsed = await response.clone().json();
            errorMessage = typeof parsed?.error === "string" ? parsed.error : undefined;
          } else if (typeof error.message === "string") {
            const jsonMatch = error.message.match(/\{.*\}$/s);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              errorMessage = typeof parsed?.error === "string" ? parsed.error : undefined;
            }
          }
        } catch {
          // Could not parse error body
        }

        const message =
          statusCode === 429
            ? errorMessage ?? "Too many requests. Please try again later."
            : errorMessage ?? "Something went wrong — please try again.";

        if (statusCode === 429) {
          setRateLimitError(message);
        }

        toast.error(message);
        return;
      }

      // Add 'whatsapp' to notification_channels when user opts in
      const { data: currentProfile } = await supabase
        .from("profiles")
        .select("notification_channels")
        .eq("user_id", user.id)
        .maybeSingle();

      const channels: string[] = currentProfile?.notification_channels || [];
      if (!channels.includes("whatsapp")) {
        await supabase
          .from("profiles")
          .update({ notification_channels: [...channels, "whatsapp"] })
          .eq("user_id", user.id);
      }

      setShowPanel(false);
      setPhoneInput("");
      setRateLimitError(null);
      onStateChange();
      toast.success("Confirmation sent — check your WhatsApp!");
    } catch {
      toast.error("That didn't work — give it another go.");
    } finally {
      setSending(false);
    }
  };

  const handleTurnOff = async () => {
    if (!user) return;
    setTurningOff(true);

    // Fetch current notification_channels to remove 'whatsapp'
    const { data: currentProfile } = await supabase
      .from("profiles")
      .select("notification_channels")
      .eq("user_id", user.id)
      .maybeSingle();

    const currentChannels: string[] = currentProfile?.notification_channels || [];
    const updatedChannels = currentChannels.filter((c: string) => c !== "whatsapp");

    const { error } = await supabase
      .from("profiles")
      .update({
        whatsapp_confirmed: false,
        whatsapp_number: null,
        notification_channels: updatedChannels,
      })
      .eq("user_id", user.id);

    setTurningOff(false);
    setShowTurnOffConfirm(false);
    if (!error) {
      onStateChange();
      toast.success("WhatsApp reminders turned off.");
    } else {
      toast.error("That didn't work — give it another go.");
    }
  };

  const handleChangeNumber = () => {
    setRateLimitError(null);
    setPhoneInput("");
    setShowPanel(true);
  };

  // Toggle visual state
  const toggleChecked = state === "confirmed";

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 space-y-2"
      onClick={!hasWhatsApp ? () => setPaywallOpen(true) : undefined}
      style={!hasWhatsApp ? { cursor: 'pointer' } : undefined}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">
            WhatsApp Reminders
            {!hasWhatsApp && <PlusBadge />}
          </p>
          {hasWhatsApp ? (
            <>
              {state === "not_opted_in" && !showPanel && (
                <p className="text-xs text-muted-foreground">
                  Get reminders sent directly to your WhatsApp
                </p>
              )}
              {state === "pending" && !showPanel && (
                <p className="text-xs" style={{ color: "#C9A84C" }}>
                  Awaiting confirmation — check your WhatsApp 💛
                </p>
              )}
              {state === "confirmed" && !showPanel && (
                <p className="text-xs text-muted-foreground">
                  {whatsappNumber ? maskNumber(whatsappNumber) : "Active"}
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Get reminders sent directly to your WhatsApp
            </p>
          )}
        </div>
        <Switch
          checked={toggleChecked}
          onCheckedChange={handleToggle}
          disabled={!hasWhatsApp || sending || turningOff || showPanel}
          className="data-[state=checked]:bg-[#C9A84C]"
        />
      </div>

      {/* Change number link for pending/confirmed */}
      {hasWhatsApp && (state === "pending" || state === "confirmed") && !showPanel && !showTurnOffConfirm && (
        <button
          onClick={handleChangeNumber}
          className="text-xs text-muted-foreground underline hover:text-foreground transition-colors"
        >
          Change number
        </button>
      )}

      {/* Turn off confirmation dialog */}
      {showTurnOffConfirm && (
        <div className="mt-2 border-l-[3px] border-l-[#C9A84C] p-4">
          <p className="text-sm font-bold text-foreground">Turn off WhatsApp reminders?</p>
          <p className="text-xs mt-1 text-muted-foreground">
            You'll go back to receiving reminders by email only.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleTurnOff}
              disabled={turningOff}
              variant="destructive"
              size="sm"
              className="rounded-full"
            >
              {turningOff ? "Turning off…" : "Turn off"}
            </Button>
            <Button
              onClick={() => setShowTurnOffConfirm(false)}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Phone input panel */}
      {hasWhatsApp && showPanel && (
        <div className="mt-2 border-l-[3px] border-l-[#C9A84C] p-4">
          <p className="text-sm font-bold text-foreground">Your WhatsApp number</p>
          <p className="text-xs mt-1 text-muted-foreground">
            We'll send a confirmation message to activate.
          </p>

          <div className="flex gap-2 mt-3">
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger className="w-[110px] rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_CODES.map((cc) => (
                  <SelectItem key={cc.value} value={cc.value}>
                    {cc.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="tel"
              inputMode="numeric"
              placeholder="e.g. 9123 4567"
              value={phoneInput}
              onChange={(e) => {
                setPhoneInput(e.target.value.replace(/[^\d\s]/g, ""));
                setRateLimitError(null);
              }}
              className="flex-1 rounded-full"
            />
          </div>

          {(() => {
            const digits = phoneInput.replace(/[\s\-()]/g, "");
            const expected = PHONE_LENGTH_MAP[countryCode];
            if (expected && digits.length >= 6 && digits.length !== expected) {
              return (
                <p className="text-xs text-red-500 mt-2">
                  Please enter a valid {COUNTRY_NAME_MAP[countryCode] || ''} number — should be {expected} digits
                </p>
              );
            }
            return null;
          })()}

          {rateLimitError && (
            <p className="text-xs text-destructive mt-2">{rateLimitError}</p>
          )}

          <Button
            onClick={handleSendConfirmation}
            disabled={
              !phoneInput.replace(/\s/g, "") ||
              sending ||
              (() => {
                const digits = phoneInput.replace(/[\s\-()]/g, "");
                const expected = PHONE_LENGTH_MAP[countryCode];
                return !!(expected && digits.length >= 6 && digits.length !== expected);
              })()
            }
            className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 mt-3"
            size="lg"
          >
            {sending ? "Sending…" : "Send confirmation"}
          </Button>

          <p className="text-center mt-2">
            <button
              onClick={() => {
                setShowPanel(false);
                setRateLimitError(null);
                setPhoneInput("");
              }}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </p>
        </div>
      )}

      {/* Render outside clickable container to prevent event bubbling from reopening */}
      <div onClick={(e) => e.stopPropagation()}>
        <PaywallModal
          isOpen={paywallOpen}
          onClose={() => setPaywallOpen(false)}
          title="WhatsApp reminders is a Circle Plus feature"
          benefit="Get reminders delivered directly to WhatsApp — the most reliable way to never miss an important date."
        />
      </div>
    </div>
  );
};

export default WhatsAppOptInPanel;
